import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { Shell } from "./components/Shell";
import Login from "./components/Login";
import ChangePassword from "./components/ChangePassword";
import EmployeeView from "./components/EmployeeView";
import AdminView from "./components/AdminView";
import InstallPrompt from "./components/InstallPrompt";
import { Auth, Users, Requests } from "./lib/api";
import { s } from "./lib/theme";

export default function App() {
  const [me, setMe] = useState(null);          // null = checking, false = not signed in, object = signed in
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);

  const refreshAll = useCallback(async () => {
    if (!me) return;
    try {
      const reqs = await Requests.list();
      setRequests(reqs);
    } catch (_e) {
      // ignore — keep previous list visible
    }
    if (me.role === "admin") {
      try {
        const us = await Users.list();
        setUsers(us);
      } catch (_e) {
        // ignore
      }
    } else {
      setUsers([me]);
    }
    try {
      const fresh = await Auth.me();
      setMe(fresh);
    } catch (_e) {
      // ignore — session refresh is best-effort
    }
  }, [me]);

  // Initial session check
  useEffect(() => {
    (async () => {
      try {
        const u = await Auth.me();
        setMe(u);
      } catch (_e) {
        setMe(false);
      }
    })();
  }, []);

  // Load data after authentication
  const meId = me && typeof me === "object" ? me.id : null;
  const meRole = me && typeof me === "object" ? me.role : null;
  const meMustChange = me && typeof me === "object" ? me.must_change_password : null;
  const meBalance = me && typeof me === "object" ? me.balance_hours : null;
  useEffect(() => {
    if (!meId) return;
    let cancelled = false;
    (async () => {
      try {
        const reqs = await Requests.list();
        if (!cancelled) setRequests(reqs);
      } catch (_e) {
        // ignore
      }
      if (meRole === "admin") {
        try {
          const us = await Users.list();
          if (!cancelled) setUsers(us);
        } catch (_e) {
          // ignore
        }
      } else if (!cancelled) {
        setUsers((prev) => (prev.length === 1 && prev[0]?.id === meId ? prev : []));
      }
    })();
    return () => { cancelled = true; };
  }, [meId, meRole, meMustChange, meBalance]);

  if (me === null) {
    return <Shell><div style={s.muted}>Loading…</div></Shell>;
  }

  if (me === false) {
    return <Login onLoggedIn={(u) => setMe(u)} />;
  }

  if (me.must_change_password) {
    return (
      <ChangePassword
        user={me}
        forced
        onSaved={(u) => setMe(u)}
        onCancel={async () => {
          try { await Auth.logout(); } catch (_e) { /* ignore */ }
          setMe(false);
        }}
      />
    );
  }

  const onLogout = async () => {
    try { await Auth.logout(); } catch (_e) { /* ignore */ }
    setMe(false);
    setUsers([]);
    setRequests([]);
  };

  return (
    <>
      <InstallPrompt />
      {me.role === "admin" ? (
        <AdminView me={me} users={users} requests={requests} refreshAll={refreshAll} onLogout={onLogout} />
      ) : (
        <EmployeeView me={me} users={users} requests={requests} refreshAll={refreshAll} onLogout={onLogout} />
      )}
    </>
  );
}
